import React, { useEffect, useState } from 'react';
import {
    X,
    Plus,
    Trash2,
    Key,
    Shield,
    Copy,
    Check,
    AlertCircle,
    User,
    Loader2
} from 'lucide-react';
import { LysepAPI } from '../../../contexts/ApiContext';
import { toast } from 'react-toastify';
import { Badge } from "../../../components/ui/Badge";

const UserSshKeysModal = ({ isOpen, onClose, serverId, user }) => {
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addingKey, setAddingKey] = useState(false);
    const [newKey, setNewKey] = useState('');
    const [copiedIndex, setCopiedIndex] = useState(null);

    const fetchKeys = async () => {
        setLoading(true);
        try {
            const resp = await LysepAPI.get(`/api/servers/${serverId}/ssh/users/${user.username}/keys`);
            if (resp.data.state) {
                setKeys(resp.data.data);
            } else {
                toast.error(`Anahtarlar alınamadı: ${resp.data.message}`);
            }
        } catch (err) {
            toast.error(`Hata: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && user) {
            fetchKeys();
        }
    }, [isOpen, user]);

    const handleAddKey = async () => {
        if (!newKey.trim()) return;
        if (!newKey.includes('ssh-')) {
            toast.warn('Geçersiz SSH public key formatı.');
            return;
        }

        setAddingKey(true);
        try {
            const resp = await LysepAPI.post(`/api/servers/${serverId}/ssh/users/${user.username}/keys`, {
                publicKey: newKey.trim()
            });
            if (resp.data.state) {
                toast.success('Anahtar başarıyla eklendi.');
                setNewKey('');
                fetchKeys();
            } else {
                toast.error(`Hata: ${resp.data.message}`);
            }
        } catch (err) {
            toast.error(`Hata: ${err.message}`);
        } finally {
            setAddingKey(false);
        }
    };

    const handleDeleteKey = async (publicKey) => {
        if (!window.confirm('Bu anahtarı silmek istediğinize emin misiniz?')) return;

        try {
            const resp = await LysepAPI.delete(`/api/servers/${serverId}/ssh/users/${user.username}/keys`, {
                data: { publicKey }
            });
            if (resp.data.state) {
                toast.success('Anahtar silindi.');
                fetchKeys();
            } else {
                toast.error(`Hata: ${resp.data.message}`);
            }
        } catch (err) {
            toast.error(`Hata: ${err.message}`);
        }
    };

    const copyToClipboard = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>

            <div className="bg-[#151515] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl relative z-10 overflow-hidden animate-zoomIn">
                {/* Header */}
                <div className="p-5 border-b border-white/10 flex items-center justify-between bg-[#1a1a1a]">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-400 border border-blue-500/20">
                            <Key className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">SSH Anahtarları</h2>
                            <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                <User className="w-3 h-3" />
                                <span>{user.username} kullanıcısı için yönetiliyor</span>
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-500 hover:text-white hover:bg-white/5 rounded-lg transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {/* Add Key Section */}
                    <div className="mb-8">
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Yeni Anahtar Ekle</label>
                        <div className="flex flex-col gap-3">
                            <textarea
                                value={newKey}
                                onChange={(e) => setNewKey(e.target.value)}
                                placeholder="ssh-rsa AAAAB3NzaC1yc2EA..."
                                className="w-full h-24 bg-[#0a0a0a] border border-white/10 rounded-xl p-3 text-sm text-gray-300 font-mono focus:outline-none focus:border-blue-500/50 transition-colors resize-none"
                            ></textarea>
                            <button
                                onClick={handleAddKey}
                                disabled={addingKey || !newKey.trim()}
                                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-xl transition-all font-medium text-sm shadow-lg shadow-blue-500/20"
                            >
                                {addingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                Anahtarı Listeye Ekle
                            </button>
                        </div>
                    </div>

                    {/* Keys List */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Mevcut Anahtarlar ({keys.length})</label>
                        </div>

                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                                <Loader2 className="w-8 h-8 animate-spin mb-3 text-blue-500" />
                                <p className="text-sm tracking-wide">Anahtarlar Sorgulanıyor...</p>
                            </div>
                        ) : keys.length === 0 ? (
                            <div className="bg-[#1a1a1a] border border-dashed border-white/5 rounded-2xl py-12 flex flex-col items-center justify-center text-gray-500 italic">
                                <AlertCircle className="w-8 h-8 opacity-20 mb-2" />
                                <p className="text-sm">Henüz yetkilendirilmiş anahtar yok.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {keys.map((key, index) => {
                                    // Parse key comment if exists
                                    const keyParts = key.split(' ');
                                    const comment = keyParts.length > 2 ? keyParts[keyParts.length - 1] : 'Adsız Anahtar';
                                    const type = keyParts[0];

                                    return (
                                        <div key={index} className="group bg-[#1a1a1a] border border-white/5 hover:border-blue-500/30 rounded-xl p-4 transition-all">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1.5">
                                                        <Shield className="w-3.5 h-3.5 text-blue-400" />
                                                        <span className="text-sm font-bold text-white truncate">{comment}</span>
                                                        <Badge variant="blue" className="text-[10px] py-0">{type}</Badge>
                                                    </div>
                                                    <div className="bg-black/40 px-3 py-2 rounded-lg text-xs font-mono text-gray-400 break-all select-all">
                                                        {key.substring(0, 60)}...
                                                    </div>
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    <button
                                                        onClick={() => copyToClipboard(key, index)}
                                                        className="p-2 text-gray-500 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                                                        title="Kopyala"
                                                    >
                                                        {copiedIndex === index ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteKey(key)}
                                                        className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                                        title="Sil"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-white/10 bg-[#1a1a1a] flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
                    >
                        Kapat
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserSshKeysModal;
